import React, { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink, LayoutGrid, Table, X } from "lucide-react";
import useAuth from "../../../hooks/useAuth";
import { can } from "../../../util/accessControl";
import {
  listTemplateAccounts, listWhatsAppTemplates, getWhatsAppTemplate, syncWhatsAppTemplates,
  saveWhatsAppTemplateDraft, validateWhatsAppTemplate, discardWhatsAppTemplateDraft, mutateWhatsAppTemplate,
} from "../../../api/whatsappTemplatesApi";
import { getNotificationErrorMessage } from "../../../api/whatsappNotificationsApi";
import { Alert, Field, btnPrimary, btnSecondary, btnDanger, card, inputClass } from "../scheduling/ui";

const emptyDefinition = () => ({
  name: "", language: "en", category: "MARKETING", header: "", body: "", footer: "",
  header_examples: [], body_examples: [], buttons: [],
});
export const templatePreviewText = (text, examples = []) =>
  (text || "").replace(/\{\{([1-9][0-9]*)\}\}/g, (token, number) => examples[Number(number) - 1] || token);
export const metaTemplatesUrl = (account) =>
  `https://business.facebook.com/wa/manage/message-templates/?${new URLSearchParams({ waba_id: account.waba_id })}`;

function Examples({ kind, text, values, onChange, disabled }) {
  const numbers = [...new Set((text || "").match(/\{\{[1-9][0-9]*\}\}/g) || [])]
    .map((token) => Number(token.slice(2, -2))).filter((number) => number <= 100).sort((a, b) => a - b);
  return numbers.slice(0, 100).map((number) => <Field key={number} label={`${kind} example {{${number}}}`}>
    <input className={inputClass} value={values[number - 1] || ""} disabled={disabled} maxLength={1024}
      onChange={(e) => { const next = [...values]; next[number - 1] = e.target.value; onChange(next); }} />
  </Field>);
}

function TemplateEditorPanel({ title, busy, onClose, children }) {
  return <aside aria-label="Template editor"
    className="min-h-[34rem] min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white xl:sticky xl:top-20">
      <header className="flex shrink-0 items-center justify-between gap-4 border-b px-5 py-4">
        <div><h3 className="text-lg font-bold">{title}</h3><p className="text-xs text-slate-500">Template preview and editor</p></div>
        <button type="button" className={btnSecondary} disabled={busy} aria-label="Close template editor" onClick={onClose}><X size={18} /></button>
      </header>
      <div className="space-y-4 p-5">{children}</div>
  </aside>;
}

function TemplateManager({ botId }) {
  const { user } = useAuth();
  const canCreate = can(user, "whatsapp_templates.create");
  const canUpdate = can(user, "whatsapp_templates.update");
  const canDelete = can(user, "whatsapp_templates.delete");
  const canSync = can(user, "whatsapp_templates.sync");
  const [view, setView] = useState("cards");
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [language, setLanguage] = useState("");
  const [selected, setSelected] = useState(null);
  const [definition, setDefinition] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const listGeneration = useRef(0);
  const mutationKey = useRef(null);
  const account = accounts.find((item) => item.id === accountId);

  useEffect(() => {
    let current = true;
    listTemplateAccounts(botId).then((rows) => {
      if (current) { setAccounts(rows); setAccountId(rows[0]?.id || ""); setLoading(false); }
    }).catch((err) => { if (current) { setError(getNotificationErrorMessage(err)); setLoading(false); } });
    return () => { current = false; };
  }, [botId]);

  const load = useCallback(async () => {
    if (!accountId) return;
    const generation = ++listGeneration.current;
    setLoading(true);
    try {
      const data = await listWhatsAppTemplates(botId, accountId, { search, status, language, offset, limit: 25 });
      if (generation === listGeneration.current) { setItems(data.items); setTotal(data.total); }
    } catch (err) {
      if (generation === listGeneration.current) setError(getNotificationErrorMessage(err));
    } finally { if (generation === listGeneration.current) setLoading(false); }
  }, [botId, accountId, search, status, language, offset]);
  useEffect(() => { load(); return () => { listGeneration.current += 1; }; }, [load]);

  const setTemplate = (row) => {
    setSelected(row);
    setDefinition(row?.editable_definition || null);
    setDirty(false);
    mutationKey.current = null;
  };
  const canLeave = () => !dirty || window.confirm("Discard unsaved template changes?");
  const closeEditor = () => {
    if (!busy && canLeave()) setTemplate(null);
  };
  const run = async (action) => {
    setBusy(true); setError(""); setMessage("");
    try { await action(); }
    catch (err) { setError(getNotificationErrorMessage(err)); }
    finally { setBusy(false); }
  };
  const open = (id) => {
    if (!canLeave()) return;
    run(async () => setTemplate(await getWhatsAppTemplate(botId, accountId, id)));
  };
  const change = (key, value) => {
    setDefinition((old) => ({ ...old, [key]: value }));
    setDirty(true);
    mutationKey.current = null;
  };
  const changeText = (kind, value) => {
    const matches = [...value.matchAll(/\{\{([1-9][0-9]*)\}\}/g)].map((m) => Number(m[1]));
    const count = Math.min(100, Math.max(0, ...matches));
    setDefinition((old) => ({ ...old, [kind]: value,
      [`${kind}_examples`]: Array.from({ length: count }, (_, i) => old[`${kind}_examples`][i] || "") }));
    setDirty(true);
    mutationKey.current = null;
  };
  const save = () => run(async () => {
    const saved = await saveWhatsAppTemplateDraft(botId, accountId, selected?.id, definition, selected?.revision);
    setTemplate(await getWhatsAppTemplate(botId, accountId, saved.id));
    setMessage("Draft saved locally. It has not been submitted to Meta.");
    await load();
  });
  const sync = () => {
    if (!canLeave()) return;
    run(async () => {
      const result = await syncWhatsAppTemplates(botId, accountId);
      if (selected?.id) setTemplate(await getWhatsAppTemplate(botId, accountId, selected.id));
      setMessage(`Synced ${result.count} templates. Reconciled ${result.reconciled} operations.`);
      if (result.unresolved) setError(`${result.unresolved} operation(s) remain uncertain. Verify in WhatsApp Manager before retrying.`);
      await load();
    });
  };
  const mutate = (action) => {
    if (!selected || dirty) return;
    const deleting = action === "delete-from-meta";
    if (!window.confirm(`${deleting ? "Delete this language variant from Meta" : "Submit this template to Meta"} for WABA ${account.waba_id}? This affects all agents using this WABA.${deleting ? "" : " Approval is controlled by Meta."}`)) return;
    const confirmName = deleting ? window.prompt("Type the exact template name to confirm deletion:") : null;
    if (deleting && confirmName !== selected.name) {
      setError("Deletion cancelled: template name did not match."); return;
    }
    run(async () => {
      if (!mutationKey.current || mutationKey.current.action !== action) {
        mutationKey.current = { action, key: window.crypto.randomUUID() };
      }
      let result;
      try {
        result = await mutateWhatsAppTemplate(botId, accountId, selected.id, action, {
          expected_revision: selected.revision, idempotency_key: mutationKey.current.key, confirm_name: confirmName,
        });
      } catch (err) {
        if (err?.response?.data?.data?.operation) {
          setTemplate(await getWhatsAppTemplate(botId, accountId, selected.id));
        }
        throw err;
      }
      const row = await getWhatsAppTemplate(botId, accountId, selected.id);
      setTemplate(row);
      if (result.operation.status !== "succeeded") {
        setError(result.operation.error_message_safe || `Operation is ${result.operation.status}. Sync to reconcile.`);
      } else {
        setMessage(deleting ? "Template deleted from Meta." : "Submitted to Meta. Sync to retrieve the latest definition and review status.");
      }
      await load();
    });
  };
  const discard = () => {
    if (!window.confirm("Discard this local draft? This will not delete a template from Meta.")) return;
    run(async () => {
      await discardWhatsAppTemplateDraft(botId, accountId, selected.id, selected.revision);
      setTemplate(selected.meta_template_id ? await getWhatsAppTemplate(botId, accountId, selected.id) : null);
      setMessage("Local draft discarded."); await load();
    });
  };
  const activeOperation = selected?.operations?.some((op) => ["pending", "running", "uncertain"].includes(op.status));
  const rejectionReason = selected?.rejection_reason?.trim();
  const canEdit = selected ? canUpdate : canCreate;
  const readOnly = !canEdit || busy || activeOperation || !!selected?.missing_from_meta_at;
  const updateButton = (index, key, value) =>
    change("buttons", definition.buttons.map((button, i) => i === index ? { ...button, [key]: value } : button));
  const editorOpen = !!(definition || selected);
  const rowStatus = (row) => row.missing_from_meta_at ? "Missing from Meta" : row.meta_status || "DRAFT";

  return <section className="space-y-4" aria-label="WhatsApp Templates">
    <div className={card}>
      <h2 className="text-lg font-bold">WhatsApp Templates</h2>
      <p className="my-2 text-sm text-amber-800">Templates belong to a WABA and are shared by all connected agents. Available actions depend on your WhatsApp Templates permissions.</p>
      <p className="mb-3 text-sm text-slate-600">Create marketing/utility text templates with positional variables and buttons. Other imported formats are read-only. Meta controls approval.</p>
      <Field label="WhatsApp account / WABA">
        <select className={inputClass} value={accountId} disabled={busy} onChange={(e) => {
          if (!canLeave()) return;
          setAccountId(e.target.value); setTemplate(null); setOffset(0); setItems([]); setError(""); setMessage("");
        }}>
          {!accounts.length && <option value="">No connected WhatsApp account</option>}
          {accounts.map((item) => <option key={item.id} value={item.id}>
            {item.display_phone_number || item.verified_name || item.phone_number_id} / WABA {item.waba_id}
          </option>)}
        </select>
      </Field>
      <div className="mt-3 flex flex-wrap gap-2">
        {canSync && <button type="button" className={btnSecondary} disabled={!accountId || busy || loading} onClick={sync}>Sync from Meta</button>}
        {canCreate && <button type="button" className={btnPrimary} disabled={!accountId || busy} onClick={() => {
          if (!canLeave()) return;
          setTemplate(null); setDefinition(emptyDefinition()); setDirty(true); setError(""); setMessage("");
        }}>New template</button>}
        <button type="button" className={btnSecondary} disabled={!accountId || busy || loading} onClick={load}>Reload list</button>
        {account?.waba_id && <a className={`${btnSecondary} inline-flex items-center gap-1.5`} href={metaTemplatesUrl(account)} target="_blank" rel="noopener noreferrer">
          Meta | WhatsApp Manager <ExternalLink size={14} aria-hidden="true" />
        </a>}
      </div>
    </div>
    {!editorOpen && <><Alert>{error}</Alert><Alert kind="success">{message}</Alert></>}
    {loading && <p role="status">Loading templates...</p>}
    {!loading && !accounts.length && <p>Connect a WhatsApp account before managing templates.</p>}
    <div aria-label="Template workspace" className={`grid items-start gap-5 ${editorOpen ? "xl:grid-cols-[280px_minmax(0,1fr)] 2xl:grid-cols-[320px_minmax(0,1fr)]" : ""}`}>
    {accountId && <div className={`${card} min-w-0 space-y-3`}>
      <div className={`grid gap-3 sm:grid-cols-3 ${editorOpen ? "xl:grid-cols-1" : ""}`}>
        <Field label="Search template name"><input className={inputClass} value={search} onChange={(e) => { setSearch(e.target.value); setOffset(0); }} /></Field>
        <Field label="Status"><select className={inputClass} value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }}>
          <option value="">All statuses</option>
          {["DRAFT", "APPROVED", "PENDING", "REJECTED", "PAUSED", "DISABLED"].map((value) => <option key={value}>{value}</option>)}
        </select></Field>
        <Field label="Language filter"><input className={inputClass} value={language} placeholder="e.g. en, en_US, hi" onChange={(e) => { setLanguage(e.target.value); setOffset(0); }} /></Field>
      </div>
      <div role="group" aria-label="Template view" className="flex justify-end gap-2">
        {[["cards", "Cards", LayoutGrid], ["table", "Table", Table]].map(([value, label, Icon]) =>
          <button type="button" key={value} className={view === value ? btnPrimary : btnSecondary}
            aria-pressed={view === value} onClick={() => setView(value)}><Icon size={16} />{label}</button>)}
      </div>
      {view === "cards" ? <div className={`grid gap-4 ${editorOpen ? "sm:grid-cols-2 xl:grid-cols-1" : "md:grid-cols-2 xl:grid-cols-3"}`}>
        {items.map((row) => <article key={row.id} aria-label={`${row.name} template`} className="flex flex-col gap-3 rounded-xl border border-slate-200 p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <h3 className="break-all font-semibold text-slate-900">{row.name}</h3>
            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold">{rowStatus(row)}</span>
          </div>
          <p className="text-xs text-slate-500">{row.language_code} / {row.category || row.draft_definition?.category || "Uncategorised"}</p>
          {row.draft_definition && <span className="text-xs font-semibold text-amber-700">Local draft</span>}
          <p className="line-clamp-4 min-h-[5rem] whitespace-pre-wrap break-words rounded-lg bg-emerald-50 p-3 text-sm text-slate-700">
            {row.draft_definition?.body || row.components?.find((part) => part.type?.toUpperCase() === "BODY")?.text || "Open to preview this template."}
          </p>
          <p className="mt-auto text-xs text-slate-500">Last sync: {row.last_synced_at ? new Date(row.last_synced_at).toLocaleString() : "Not synced"}</p>
          <button type="button" className={btnSecondary} disabled={busy} onClick={() => open(row.id)}>Open {row.name}</button>
        </article>)}
      </div> : <div className="overflow-x-auto"><table aria-label="Templates" className="w-full text-left text-sm">
        <thead><tr>{["Name", "Language", "Category", "Status", "Last sync", ""].map((label, i) => <th key={i} className="p-2">{label}</th>)}</tr></thead>
        <tbody>{items.map((row) => <tr key={row.id} className="border-t">
          <td className="p-2">{row.name}{row.draft_definition && <span className="ml-2 text-amber-700">(draft)</span>}</td>
          <td className="p-2">{row.language_code}</td><td className="p-2">{row.category || row.draft_definition?.category}</td>
          <td className="p-2">{row.missing_from_meta_at ? "Missing from Meta" : row.meta_status || "DRAFT"}</td>
          <td className="p-2">{row.last_synced_at ? new Date(row.last_synced_at).toLocaleString() : "Not synced"}</td>
          <td className="p-2"><button type="button" className={btnSecondary} disabled={busy} onClick={() => open(row.id)}>Open {row.name}</button></td>
        </tr>)}</tbody>
      </table></div>}
      {!loading && !items.length && <p>No templates found. Sync from Meta or create a new template.</p>}
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className={btnSecondary} disabled={offset === 0 || loading || busy} onClick={() => setOffset(Math.max(0, offset - 25))}>Previous</button>
        <span>{total} templates</span>
        <button type="button" className={btnSecondary} disabled={offset + 25 >= total || loading || busy} onClick={() => setOffset(offset + 25)}>Next</button>
      </div>
    </div>}
    {editorOpen && <TemplateEditorPanel title={selected ? `Edit template: ${selected.name}` : "New template draft"} busy={busy} onClose={closeEditor}>
      <p className="text-sm text-amber-800">WABA {account?.waba_id}. Changes affect all agents using this WABA.</p>
      <Alert>{error}</Alert><Alert kind="success">{message}</Alert>
      {canSync && <button type="button" className={btnSecondary} disabled={busy} onClick={sync}>Sync from Meta</button>}
      {selected?.meta_status === "REJECTED" && rejectionReason && rejectionReason.toUpperCase() !== "NONE" &&
        <Alert>Meta rejection: {rejectionReason}</Alert>}
      {selected?.missing_from_meta_at && <Alert>This template is missing from Meta. Sync before making changes.</Alert>}
      {activeOperation && <Alert>An operation is active or uncertain. Wait for completion, then sync to reconcile. Do not blindly retry.</Alert>}
      {!definition && <p>This format is read-only here. Manage it in WhatsApp Manager.</p>}
      {definition && <div role="group" aria-label="Template edit and preview" className="grid items-start gap-6 md:grid-cols-2">
        <fieldset disabled={readOnly} className="min-w-0 space-y-3">
          <Field label="Template name"><input className={inputClass} value={definition.name} disabled={!!selected} maxLength={512} onChange={(e) => change("name", e.target.value)} /></Field>
          <Field label="Language code"><input className={inputClass} value={definition.language} disabled={!!selected} maxLength={20} onChange={(e) => change("language", e.target.value)} /></Field>
          <Field label="Category"><select className={inputClass} value={definition.category} onChange={(e) => change("category", e.target.value)}>
            <option value="MARKETING">Marketing</option><option value="UTILITY">Utility</option>
          </select></Field>
          <Field label="Header (optional)"><input className={inputClass} value={definition.header} maxLength={60} onChange={(e) => changeText("header", e.target.value)} /></Field>
          <Examples kind="Header" text={definition.header} values={definition.header_examples} disabled={readOnly} onChange={(v) => change("header_examples", v)} />
          <Field label="Body" hint="Use {{1}}, {{2}}, etc. Supply examples for Meta review, not runtime customer mappings.">
            <textarea aria-label="Body" className={inputClass} rows={6} maxLength={1024} value={definition.body} onChange={(e) => changeText("body", e.target.value)} />
          </Field>
          <Examples kind="Body" text={definition.body} values={definition.body_examples} disabled={readOnly} onChange={(v) => change("body_examples", v)} />
          <Field label="Footer (optional)"><input className={inputClass} value={definition.footer} maxLength={60} onChange={(e) => change("footer", e.target.value)} /></Field>
          {definition.buttons.map((button, index) => <div key={index} className="space-y-2 rounded-xl border p-3">
            <Field label={`Button ${index + 1} type`}><select className={inputClass} value={button.type} onChange={(e) => updateButton(index, "type", e.target.value)}>
              <option value="QUICK_REPLY">Quick reply</option><option value="URL">URL</option><option value="PHONE_NUMBER">Phone</option>
            </select></Field>
            <Field label={`Button ${index + 1} text`}><input className={inputClass} value={button.text} maxLength={25} onChange={(e) => updateButton(index, "text", e.target.value)} /></Field>
            {button.type === "URL" && <>
              <Field label={`Button ${index + 1} URL`}><input className={inputClass} value={button.url || ""} onChange={(e) => updateButton(index, "url", e.target.value)} /></Field>
              <Field label={`Button ${index + 1} example URL`} hint="Required only for a URL ending in {{1}}."><input className={inputClass} value={button.example || ""} onChange={(e) => updateButton(index, "example", e.target.value)} /></Field>
            </>}
            {button.type === "PHONE_NUMBER" && <Field label={`Button ${index + 1} phone`}><input className={inputClass} value={button.phone_number || ""} onChange={(e) => updateButton(index, "phone_number", e.target.value)} /></Field>}
            <button type="button" className={btnSecondary} onClick={() => change("buttons", definition.buttons.filter((_, i) => i !== index))}>Remove button {index + 1}</button>
          </div>)}
          <button type="button" className={btnSecondary} disabled={definition.buttons.length >= 3} onClick={() => change("buttons", [...definition.buttons, { type: "QUICK_REPLY", text: "", url: "", phone_number: "", example: "" }])}>Add button</button>
        </fieldset>
        <section aria-label="Live template preview" className="min-w-0 md:sticky md:top-24">
          <h4 className="mb-2 font-semibold">Preview with review examples</h4>
          <div className="space-y-3 break-words rounded-xl bg-emerald-50 p-5 whitespace-pre-wrap">
            <p className="font-bold">{templatePreviewText(definition.header, definition.header_examples)}</p>
            <p>{templatePreviewText(definition.body, definition.body_examples)}</p>
            <p className="text-sm text-slate-500">{definition.footer}</p>
            {definition.buttons.map((button, index) => <div key={index} className="rounded-lg border bg-white p-2 text-center text-blue-700">{button.text || "Button"}</div>)}
          </div>
          <p className="mt-2 text-xs text-slate-500">Approximate preview. Saving a draft does not submit or send a message.</p>
        </section>
      </div>}
      <div className="flex flex-wrap gap-2">
        {definition && <>
          {canUpdate && <button type="button" className={btnSecondary} disabled={readOnly} onClick={() => run(async () => {
            await validateWhatsAppTemplate(botId, accountId, definition); setMessage("Definition validated locally. Meta may apply additional restrictions.");
          })}>Validate</button>}
          {canEdit && <button type="button" className={btnPrimary} disabled={readOnly || (!dirty && !!selected)} onClick={save}>Save draft</button>}
          {canUpdate && <button type="button" className={btnPrimary} disabled={readOnly || dirty || !selected?.draft_definition}
            onClick={() => mutate(selected.meta_template_id ? "submit-update" : "submit")}>Submit to Meta</button>}
        </>}
        {canDelete && selected?.draft_definition && <button type="button" className={btnSecondary} disabled={busy || activeOperation} onClick={discard}>Discard local draft</button>}
        {canDelete && selected?.meta_template_id && <button type="button" className={btnDanger} disabled={busy || activeOperation || dirty || !!selected.missing_from_meta_at} onClick={() => mutate("delete-from-meta")}>Delete from Meta</button>}
      </div>
      {selected?.operations?.length > 0 && <div>
        <h4 className="font-semibold">Recent operations</h4>
        {selected.operations.map((op) => <p key={op.id} className="mt-2 text-sm">
          {op.operation_type}: <strong>{op.status}</strong> - {new Date(op.created_at).toLocaleString()}
          {op.error_message_safe && <span className="block text-rose-700">{op.error_message_safe}</span>}
        </p>)}
      </div>}
      {selected?.meta_template_id && <details><summary className="cursor-pointer text-sm">Last Meta definition (read-only)</summary><pre className="mt-2 overflow-auto text-xs">{JSON.stringify(selected.meta_snapshot, null, 2)}</pre></details>}
    </TemplateEditorPanel>}
    </div>
  </section>;
}

export default function WhatsAppTemplatesPanel({ botId }) {
  const { user } = useAuth();
  if (!can(user, "whatsapp_templates.read")) return <p role="alert">Access denied. You do not have permission to view WhatsApp templates.</p>;
  return <TemplateManager key={botId} botId={botId} />;
}
